import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Member } from '@cloudsign/shared';
import type { Field } from '@cloudsign/shared';
import { Lock, X } from 'lucide-react';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { FileDrop } from '../../components/FileDrop';
import type { FileDropResult } from '../../components/FileDrop';
import { ErrorPanel, errorMessage, fieldErrors } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Alert, Button, Spinner, TextField } from '../../components/ui';
import { FieldInput, checkAnswer, rawFromValue } from '../FieldInput';
import type { Raw } from '../FieldInput';
import { CompanionMemberTab } from '../people/CompanionAdmin';
import { useModuleOn } from '../people/api';
import { MemberMusterPoint } from '../evacuation/MemberMusterPoint';
import { MemberCardsTab } from '../safety/MemberCardsTab';
import { MemberDocumentsTab } from '../safety/MemberDocumentsTab';
import { LoneWorkContactsTab } from '../permits/lone/ContactsTab';
import { useGroupFields } from './hooks';

type Values = Record<string, Raw>;

/** Thrown to stop a save when the registry finds a problem with an answer; the problems show beside the questions. */
class LocalProblem extends Error {}

const PHOTO_SIZES = [
  { width: 960, height: 960, mode: 'fit' as const },
  { width: 160, height: 160, mode: 'cover' as const },
];

type DrawerTab = 'app' | 'documents' | 'cards' | 'musterPoint' | 'loneWorking';

export function MemberDrawer({ groupId, memberId, onClose }: { groupId: string; memberId: string | null; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['members', 'one', memberId],
    queryFn: () => api.get(`/members/${memberId}`, { schema: Member }),
    enabled: memberId !== null,
  });
  const fields = useGroupFields(groupId);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const companionOn = useModuleOn('companion');
  const canManageMembers = useCan('groups.manage_members');
  // Phase 3 tabs: each shows only when its module is on and the person may use it (the server still checks every call).
  const complianceMod = useModuleOn('compliance');
  const canDocs = useCan('compliance.view');
  const cardsMod = useModuleOn('nfc-cards');
  const canCards = useCan('nfc_cards.view');
  const evacuationMod = useModuleOn('evacuation');
  const canEvac = useCan('evacuation.view');
  const canEditMuster = useCan('evacuation.manage');
  const loneMod = useModuleOn('lone-working');
  const canLone = useCan('lone_working.manage');
  const tabs: DrawerTab[] = [];
  if (memberId !== null) {
    if (companionOn && canManageMembers) tabs.push('app');
    if (complianceMod && canDocs) tabs.push('documents');
    if (cardsMod && canCards) tabs.push('cards');
    if (evacuationMod && canEvac) tabs.push('musterPoint');
    if (loneMod && canLone) tabs.push('loneWorking');
  }
  const showTabs = tabs.length > 0;
  const [tab, setTab] = useState<DrawerTab | 'details'>('details');
  const title = memberId === null ? t('membersUi.drawer.new') : t('membersUi.drawer.edit', { name: q.data?.fullName ?? '' });
  const ready = memberId === null || q.data;
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-slate-900/40">
      <aside role="dialog" aria-modal="true" aria-label={title} className="h-full w-full max-w-lg overflow-y-auto bg-white p-5 shadow-xl">
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button type="button" aria-label={t('membersUi.drawer.close')} onClick={onClose} className="inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-slate-100">
            <X aria-hidden="true" className="h-5 w-5" />
          </button>
        </div>
        {q.error ? (
          <ErrorPanel error={q.error} />
        ) : !ready || fields.isPending ? (
          <Spinner />
        ) : (
          <>
            {showTabs ? (
              <div role="tablist" aria-label={title} className="mb-4 flex flex-wrap gap-1 border-b border-slate-200">
                {(['details', ...tabs] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    role="tab"
                    id={`member-tab-${k}`}
                    aria-selected={tab === k}
                    aria-controls={`member-tabpanel-${k}`}
                    onClick={() => setTab(k)}
                    className={`-mb-px min-h-10 border-b-2 px-4 text-sm font-medium ${tab === k ? 'border-[var(--brand-primary)] text-slate-900' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
                  >
                    {t(`membersUi.drawer.tab.${k}`)}
                  </button>
                ))}
              </div>
            ) : null}
            <div role={showTabs ? 'tabpanel' : undefined} id="member-tabpanel-details" aria-labelledby={showTabs ? 'member-tab-details' : undefined} hidden={tab !== 'details'}>
              <DrawerForm groupId={groupId} member={q.data ?? null} fields={(fields.data ?? []).filter((f) => f.status === 'active')} onClose={onClose} />
            </div>
            {memberId && tabs.includes('app') ? (
              <div role="tabpanel" id="member-tabpanel-app" aria-labelledby="member-tab-app" hidden={tab !== 'app'}>
                <CompanionMemberTab memberId={memberId} />
              </div>
            ) : null}
            {memberId && tab === 'documents' && tabs.includes('documents') ? (
              <div role="tabpanel" id="member-tabpanel-documents" aria-labelledby="member-tab-documents">
                <MemberDocumentsTab memberId={memberId} />
              </div>
            ) : null}
            {memberId && tab === 'cards' && tabs.includes('cards') ? (
              <div role="tabpanel" id="member-tabpanel-cards" aria-labelledby="member-tab-cards">
                <MemberCardsTab memberId={memberId} />
              </div>
            ) : null}
            {memberId && tab === 'musterPoint' && tabs.includes('musterPoint') ? (
              <div role="tabpanel" id="member-tabpanel-musterPoint" aria-labelledby="member-tab-musterPoint">
                <MemberMusterPoint memberId={memberId} canEdit={canEditMuster} />
              </div>
            ) : null}
            {memberId && tab === 'loneWorking' && tabs.includes('loneWorking') ? (
              <div role="tabpanel" id="member-tabpanel-loneWorking" aria-labelledby="member-tab-loneWorking">
                <LoneWorkContactsTab memberId={memberId} />
              </div>
            ) : null}
          </>
        )}
      </aside>
    </div>
  );
}

function DrawerForm({ groupId, member, fields, onClose }: { groupId: string; member: Member | null; fields: Field[]; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const canSensitive = useCan('groups.manage_sensitive');
  const [name, setName] = useState(member?.fullName ?? '');
  const [email, setEmail] = useState(member?.email ?? '');
  const [phone, setPhone] = useState(member?.phone ?? '');
  const [role, setRole] = useState(member?.role ?? '');
  const [externalId, setExternalId] = useState(member?.externalId ?? '');
  const [startDate, setStartDate] = useState(member?.startDate ?? '');
  const [endDate, setEndDate] = useState(member?.endDate ?? '');
  const [values, setValues] = useState<Values>(() => {
    const v: Values = {};
    for (const f of member?.fields ?? []) {
      const def = fields.find((x) => x.id === f.fieldId);
      if (def && !f.redacted) v[f.fieldId] = rawFromValue(def, f.value);
    }
    return v;
  });
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState<string | null>(null);
  const [problems, setProblems] = useState<Record<string, string>>({});

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['members'] });
  };
  const locked = (f: Field) => f.sensitive && !canSensitive;

  const save = useMutation({
    mutationFn: async () => {
      const custom: Record<string, unknown> = {};
      const found: Record<string, string> = {};
      for (const f of fields) {
        if (locked(f)) continue;
        const r = checkAnswer(f, values[f.id]);
        if (!r.ok) found[f.id] = r.messageKey;
        else custom[f.id] = r.value;
      }
      setProblems(found);
      if (Object.keys(found).length > 0) throw new LocalProblem();
      const body = {
        fullName: name,
        email: email || null,
        phone: phone || null,
        role: role || null,
        externalId: externalId || null,
        startDate: startDate || null,
        endDate: endDate || null,
        fields: custom,
      };
      return member ? api.patch(`/members/${member.id}`, { body, schema: Member }) : api.post('/members', { body: { ...body, groupId }, schema: Member });
    },
    onSuccess: () => {
      toast(member ? t('membersUi.updated') : t('membersUi.created'));
      refresh();
      onClose();
    },
  });

  const setStatus = useMutation({
    mutationFn: (status: 'archived' | 'current') => api.patch(`/members/${member?.id}`, { body: { status }, schema: Member }),
    onSuccess: (_d, status) => {
      toast(status === 'archived' ? t('membersUi.archived') : t('membersUi.restored'));
      refresh();
      onClose();
    },
  });

  const uploadPhoto = useMutation({
    mutationFn: (r: FileDropResult) => {
      const form = new FormData();
      form.append('photo', r.blobs[0] as Blob, 'photo.jpg');
      form.append('thumb', r.blobs[1] as Blob, 'thumb.jpg');
      return api.post(`/members/${member?.id}/photo`, { form, schema: Member });
    },
    onSuccess: () => {
      toast(t('membersUi.photo.saved'));
      refresh();
    },
  });
  const removePhoto = useMutation({
    mutationFn: () => api.delete(`/members/${member?.id}/photo`),
    onSuccess: () => {
      toast(t('membersUi.photo.removed'));
      refresh();
    },
  });
  const setPinMut = useMutation({
    mutationFn: (p: string | null) => api.put(`/members/${member?.id}/pin`, { body: { pin: p }, schema: Member }),
    onSuccess: (_d, p) => {
      toast(p ? t('membersUi.pin.saved') : t('membersUi.pin.removed'));
      setPin('');
      refresh();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  const errs = fieldErrors(save.error);

  return (
    <div className="space-y-6">
      <form onSubmit={submit} className="space-y-4" noValidate>
        <TextField label={t('membersUi.field.fullName')} value={name} required error={errs['fullName']} onChange={(e) => setName(e.target.value)} />
        <TextField label={t('membersUi.field.email')} type="email" value={email} error={errs['email']} onChange={(e) => setEmail(e.target.value)} />
        <TextField label={t('membersUi.field.phone')} type="tel" value={phone} error={errs['phone']} onChange={(e) => setPhone(e.target.value)} />
        <TextField label={t('membersUi.field.role')} value={role} error={errs['role']} onChange={(e) => setRole(e.target.value)} />
        <TextField
          label={t('membersUi.field.externalId')}
          hint={t('membersUi.field.externalId.hint')}
          value={externalId}
          error={errs['externalId']}
          onChange={(e) => setExternalId(e.target.value)}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField label={t('membersUi.field.startDate')} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          <TextField label={t('membersUi.field.endDate')} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>

        {fields.length > 0 ? (
          <fieldset className="space-y-4">
            <legend className="mb-2 text-sm font-semibold">{t('membersUi.personalFields')}</legend>
            {fields.map((f) =>
              locked(f) ? (
                <div key={f.id} className="flex items-start gap-2 rounded-md bg-slate-50 p-3 text-sm text-slate-600">
                  <Lock aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    <span className="font-medium text-slate-800">{f.label}</span> ({t('membersUi.sensitive')}). {t('membersUi.sensitive.locked')}
                  </span>
                </div>
              ) : (
                <div key={f.id}>
                  {f.sensitive ? (
                    <p className="mb-1 flex items-center gap-1 text-xs text-slate-600">
                      <Lock aria-hidden="true" className="h-3.5 w-3.5" />
                      {t('membersUi.sensitive')}
                    </p>
                  ) : null}
                  <FieldInput field={f} raw={values[f.id] ?? ''} error={problems[f.id] ?? errs[`fields.${f.id}`]} onChange={(v) => setValues((s) => ({ ...s, [f.id]: v }))} />
                </div>
              ),
            )}
          </fieldset>
        ) : null}

        {save.error && !(save.error instanceof LocalProblem) ? <Alert>{errorMessage(save.error)}</Alert> : null}
        <div className="flex flex-wrap gap-2">
          <Button type="submit" variant="primary" disabled={save.isPending || name.trim() === ''}>
            {t('common.save')}
          </Button>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          {member ? (
            member.status === 'current' ? (
              <Button disabled={setStatus.isPending} onClick={() => setStatus.mutate('archived')}>
                {t('membersUi.archive')}
              </Button>
            ) : (
              <Button disabled={setStatus.isPending} onClick={() => setStatus.mutate('current')}>
                {t('membersUi.restore')}
              </Button>
            )
          ) : null}
        </div>
        {setStatus.error ? <Alert>{errorMessage(setStatus.error)}</Alert> : null}
      </form>

      {member ? (
        <>
          <section aria-label={t('membersUi.photo')} className="space-y-3 border-t border-slate-200 pt-4">
            <h3 className="text-sm font-semibold">{t('membersUi.photo')}</h3>
            <p className="text-sm text-slate-600">{member.photoFileId ? t('membersUi.photo.set') : t('membersUi.photo.none')}</p>
            <FileDrop
              label={t('membersUi.photo.choose')}
              sizes={PHOTO_SIZES}
              onChange={(r) => {
                if (r) uploadPhoto.mutate(r);
              }}
              disabled={uploadPhoto.isPending}
            />
            {member.photoFileId ? (
              <Button disabled={removePhoto.isPending} onClick={() => removePhoto.mutate()}>
                {t('membersUi.photo.remove')}
              </Button>
            ) : null}
            {uploadPhoto.error ? <Alert>{errorMessage(uploadPhoto.error)}</Alert> : null}
            {removePhoto.error ? <Alert>{errorMessage(removePhoto.error)}</Alert> : null}
          </section>

          <section aria-label={t('membersUi.pin')} className="space-y-3 border-t border-slate-200 pt-4">
            <h3 className="text-sm font-semibold">{t('membersUi.pin')}</h3>
            <p className="text-sm text-slate-600">{member.hasPin ? t('membersUi.pin.has') : t('membersUi.pin.none')}</p>
            <TextField
              label={t('membersUi.pin.label')}
              hint={t('membersUi.pin.hint')}
              inputMode="numeric"
              maxLength={4}
              autoComplete="off"
              value={pin}
              error={pinError ?? undefined}
              onChange={(e) => {
                setPin(e.target.value.replace(/\D/g, ''));
                setPinError(null);
              }}
            />
            <div className="flex gap-2">
              <Button
                disabled={setPinMut.isPending}
                onClick={() => {
                  if (!/^\d{4}$/.test(pin)) setPinError(t('membersUi.pin.invalid'));
                  else setPinMut.mutate(pin);
                }}
              >
                {t('membersUi.pin.set')}
              </Button>
              {member.hasPin ? (
                <Button disabled={setPinMut.isPending} onClick={() => setPinMut.mutate(null)}>
                  {t('membersUi.pin.remove')}
                </Button>
              ) : null}
            </div>
            {setPinMut.error ? <Alert>{errorMessage(setPinMut.error)}</Alert> : null}
          </section>
        </>
      ) : null}
    </div>
  );
}
